// The FIG card on What we bought: how many of the FIG card's Chase charges
// have a receipt, from the server's coverage answer by account
// (/receipts-data/coverage?account=1758). Pure; the server's figures as
// sent, nothing estimated. By account the server leaves out charges that
// never have a receipt (payroll, transfers, card payments, fees) and says
// what it dropped. A charge whose receipt is Personal is personal on the FIG
// card, not a business charge with a receipt: the headline, the percent and
// By category count business only, and personal is its own line.
import { usd } from './money';

/** FIG operating (Chase ·1758; card ·8805 is on it). */
export const FIG_COVERAGE_ACCOUNT = '1758';

const pct = (x) => `${Math.round((x || 0) * 100)}%`;
const c = (v) => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v * 100) : 0);
const d = (cents) => cents / 100;

/** Covered charges whose receipt is Personal (personal spend on the FIG card). */
export const isPersonal = (row) => row?.receiptContext === 'Personal';

/**
 * Business vs personal, in cents underneath. Personal charges always have a
 * receipt, so missing is the server's as sent. A personal charge with no
 * amount read counts as a charge and adds to no sum.
 * -> { personal: { charges, dollars, rows }, business: { charges, dollars, withReceipt, coveredDollars, missing, missingDollars, share }, all: { charges, dollars } }
 */
export function figSplit(cov) {
  const rows = (cov?.covered || []).filter(isPersonal);
  const pc = rows.reduce((a, r) => a + c(r.amount), 0);
  const dollars = c(cov?.chargeDollars) - pc;
  const coveredCents = c(cov?.chargeDollarsWithReceipt) - pc;
  return {
    personal: { charges: rows.length, dollars: d(pc), rows },
    business: {
      charges: (cov?.charges || 0) - rows.length, dollars: d(dollars),
      withReceipt: (cov?.chargesWithReceipt || 0) - rows.length, coveredDollars: d(coveredCents),
      missing: (cov?.missing || []).length, missingDollars: cov?.missingDollars ?? 0,
      share: dollars > 0 ? coveredCents / dollars : null,
    },
    all: { charges: cov?.charges || 0, dollars: cov?.chargeDollars ?? 0 },
  };
}

/** The headline (business only); { businessNone } when every charge is personal; null when the range has no charges. */
export function figHeadline(cov) {
  if (!cov || !(cov.charges > 0)) return null;
  const { business: b } = figSplit(cov);
  if (!(b.charges > 0)) return { businessNone: true };
  return {
    withReceipt: b.withReceipt, charges: b.charges,
    coveredDollars: b.coveredDollars, chargeDollars: b.dollars,
    share: b.share ?? 0,
    text: `${b.withReceipt} of ${b.charges} business charges have a receipt · ${usd(b.coveredDollars)} of ${usd(b.dollars)} · ${pct(b.share)}`,
  };
}

/** The section's collapsed line: "0 of 10 · 0%", "3 personal, no business", "No charges", or null while there is no answer. */
export function figSummary(cov) {
  if (!cov) return null;
  if (!(cov.charges > 0)) return 'No charges';
  const { business: b, personal: p } = figSplit(cov);
  if (!(b.charges > 0)) return `${p.charges} personal, no business`;
  return `${b.withReceipt} of ${b.charges} · ${pct(b.share)}`;
}

/** "Personal on the FIG card: 3 charges · $618.81 · review with the CPA", or null when there is none. */
export function personalLine(cov) {
  const { personal: p } = figSplit(cov);
  if (!p.charges) return null;
  return `Personal on the FIG card: ${p.charges} charge${p.charges === 1 ? '' : 's'} · ${usd(p.dollars)} · review with the CPA`;
}

/** "Business with a receipt $0.00 + business missing $643.78 + personal $618.81 = $1,262.59" */
export function figNote(cov) {
  const { business: b, personal: p, all } = figSplit(cov);
  return `Business with a receipt ${usd(b.coveredDollars)} + business missing ${usd(b.missingDollars)} + personal ${usd(p.dollars)} = ${usd(all.dollars)}`;
}

/**
 * By category, business only: each personal charge comes off its category,
 * and a category left with no charges is dropped. Most missing dollars
 * first; a category with nothing missing goes last with done = true.
 * -> [{ key, name, charges, covered, missing, dollars, missingDollars, done, text }]
 */
export function categoryRows(cov) {
  const off = new Map();
  for (const r of figSplit(cov).personal.rows) {
    const k = r.categoryId ?? null;
    const e = off.get(k) || { charges: 0, cents: 0 };
    e.charges++; e.cents += c(r.amount);
    off.set(k, e);
  }
  const rows = (cov?.byCategory || []).map((x) => {
    const o = off.get(x.categoryId ?? null);
    return o ? { ...x, charges: x.charges - o.charges, dollars: d(c(x.dollars) - o.cents) } : x;
  }).filter((x) => x.charges > 0).map((k) => {
    const covered = k.charges - k.missing;
    const name = k.category || 'Uncategorized';
    const done = k.missing === 0;
    return {
      key: k.categoryId ?? 'none', name, charges: k.charges, covered, missing: k.missing, dollars: k.dollars, missingDollars: k.missingDollars, done,
      text: done ? `${name} · all ${k.charges} · ${usd(k.dollars)}` : `${name} · ${covered} of ${k.charges} · ${usd(k.missingDollars)} missing`,
    };
  });
  return rows.sort((a, b) => (a.done - b.done) || (a.done ? (b.dollars || 0) - (a.dollars || 0) : (b.missingDollars || 0) - (a.missingDollars || 0)));
}

export const personalCount = (cov) => (cov?.covered || []).filter(isPersonal).length;

/** "Covered (12) · 2 Personal" */
export function coveredLine(cov) {
  const n = (cov?.covered || []).length;
  const p = personalCount(cov);
  return `Covered (${n})${p ? ` · ${p} Personal` : ''}`;
}

/** "Left out: 18 charges, $9,412.00 (payroll, transfers, card payments, fees)", or null when nothing was left out. */
export function leftOutLine(cov) {
  const x = cov?.excluded;
  if (!x || !x.applied || !(x.charges > 0)) return null;
  return `Left out: ${x.charges} charge${x.charges === 1 ? '' : 's'}, ${usd(x.dollars)} (payroll, transfers, card payments, fees)`;
}

/** The missing list's footer: total, and how many amounts were not read. */
export function missingFoot(cov) {
  const unread = cov?.missingWithoutAmount || 0;
  return `${usd(cov?.missingDollars ?? 0)}${unread ? ` (${unread} amount${unread === 1 ? '' : 's'} not read)` : ''}`;
}
